import { gql, useQuery } from '@apollo/client'
import { toFriendlyRecoveryCertificateId } from 'helpers/string'
import { useState } from 'react'
import { BulmaMessage } from '../../Bulma'
import { CertificateData } from './CertificateData'
import CertificateFiles from './CertificateFiles'
import CertificateNotes from './CertificateNotes'
import { Submit, TableSelectField } from './Fields'
import { paymentStatuses, recoveryCertificateStatuses } from './index'
import { friendlyConstant } from './RecoveryCertificate'
import { OnSubmitParams, UpdateRecoveryCertificateForm } from './UpdateRecoveryCertificateForm'
import {
  RecoveryCertificateQuery,
  RecoveryCertificateQueryVariables,
  RecoveryCertificateQuery_recoveryCertificates,
} from './__generated__/RecoveryCertificateQuery'

const recoveryCertificatesQuery = gql`
  query RecoveryCertificateModalQuery($orderId: ID, $status: [RecoveryCertificateStatus], $unClaimed: Boolean) {
    recoveryCertificates(orderId: $orderId, status: $status, unClaimed: $unClaimed) {
      orderId
      UUID

      completingFor
      completingForFirstName
      completingForLastName
      completingForRelationshipToPatient
      firstName
      lastName
      dateOfBirth
      emailAddress
      telephoneNumber
      passportNumber

      travelToDestination
      travelToDestinationOtherDescription

      positiveCovidTestDate
      typeOfCovidTest
      typeOfCovidTestDescription
      covidSymptomsAroundTestDate
      lastTimeExperiencedSymptoms
      additionalInformation

      fileUrls

      notes
      status
      paymentStatus
      claimedBy
      claimedAt

      createdAt
    }
  }
`

export const VerifyRecoveryCertificateModal = ({
  recoveryCertificateId,
  onClose,
}: {
  recoveryCertificateId: RecoveryCertificateQuery_recoveryCertificates['orderId']
  onClose: (status) => void
}) => {
  const { loading, data, refetch } = useQuery<RecoveryCertificateQuery, RecoveryCertificateQueryVariables>(
    recoveryCertificatesQuery,
    {
      variables: {
        orderId: recoveryCertificateId,
      },
      skip: !recoveryCertificateId,
      fetchPolicy: 'cache-and-network',
    }
  )

  const [errorMessage, setErrorMessage] = useState<JSX.Element>()

  const recoveryCertificate = data?.recoveryCertificates[0]

  const onSubmit = ({ result, action, status, error }: OnSubmitParams) => {
    setErrorMessage(undefined)

    if (result) {
      onClose && onClose(status)
    } else {
      setErrorMessage(
        <>
          <p>
            <b>An Error Occurred While Trying To Update The Order</b>
          </p>
          <br />
          <blockquote className="is-italic">{error.message}</blockquote>
        </>
      )
    }
  }

  return (
    <UpdateRecoveryCertificateForm
      recoveryCertificate={recoveryCertificate}
      refetchRecoveryCertificate={refetch}
      onSubmit={onSubmit}
    >
      <div className={`modal ${recoveryCertificateId && 'is-active'}`}>
        <div className="modal-background"></div>
        <div className="modal-content container force-fullwidth">
          <div className="modal-card container force-fullwidth">
            <header className="modal-card-head">
              <p className="modal-card-title">
                <strong>
                  Verify Recovery Certificate Documentation &ndash;{' '}
                  {recoveryCertificate?.orderId && toFriendlyRecoveryCertificateId(recoveryCertificate?.orderId)}
                </strong>
              </p>
              <Submit className="delete" name="action" value="UNCLAIM" />
            </header>

            <section className="modal-card-body">
              {errorMessage && <BulmaMessage color="danger">{errorMessage}</BulmaMessage>}
              {loading ? <p>Loading...</p> : <VerifyRecoveryCertificate recoveryCertificate={recoveryCertificate} />}
              {errorMessage && <BulmaMessage color="danger">{errorMessage}</BulmaMessage>}
            </section>
          </div>
        </div>
      </div>
    </UpdateRecoveryCertificateForm>
  )
}

const VerifyRecoveryCertificate = ({
  recoveryCertificate,
}: {
  recoveryCertificate: RecoveryCertificateQuery_recoveryCertificates
}) => {
  const isFinalReview = ['DOCUMENTATION_COMPLETE', 'FINAL_REVIEW'].includes(recoveryCertificate?.status)

  return (
    <>
      <div className="columns">
        <div className="column is-6">
          <article className="message">
            <div className="message-header">
              <p className="has-text-light">Status</p>
            </div>
            <div className="message-body p-0">
              <table className="table is-fullwidth is-striped is-size-7">
                <tbody>
                  <tr>
                    <th>Order ID:</th>
                    <td>
                      {recoveryCertificate?.orderId && toFriendlyRecoveryCertificateId(recoveryCertificate?.orderId)}
                    </td>
                  </tr>
                  <tr>
                    <th>UUID:</th>
                    <td>{recoveryCertificate?.UUID}</td>
                  </tr>
                  <TableSelectField
                    label="Status"
                    value={recoveryCertificate?.status}
                    name="status"
                    options={recoveryCertificateStatuses.map((status) => ({
                      label: friendlyConstant(status),
                      value: status,
                    }))}
                  />
                  <TableSelectField
                    label="Payment Status"
                    value={recoveryCertificate?.paymentStatus}
                    name="status"
                    options={paymentStatuses.map((status) => ({
                      label: friendlyConstant(status),
                      value: status,
                    }))}
                  />
                </tbody>
              </table>
            </div>
          </article>

          <CertificateData recoveryCertificate={recoveryCertificate} edit={true} />
        </div>
        <div className="column is-6">
          <article className="message is-info">
            <div className="message-header">
              <p className="has-text-light">Documentation Review</p>
            </div>
            <div className="message-body p-0">
              <BulmaMessage color="info" className="m-0">
                <p>Every action also saves any changes made on this page</p>
              </BulmaMessage>

              <table className="table is-fullwidth is-striped is-vcentered has-uniform-cellpadding has-valign-middle">
                <tbody>
                  {recoveryCertificate?.paymentStatus === 'PAID' ? (
                    <tr>
                      <td>
                        <Submit
                          className="is-success is-fullwidth"
                          name="action"
                          value={isFinalReview ? 'APPROVE' : 'DOCUMENTATION_COMPLETE'}
                        >
                          {isFinalReview ? 'Send Certificate' : 'Documentation Complete'}
                        </Submit>
                      </td>
                      <td>
                        {isFinalReview ? 'Approve and Send Certificate to Customer' : 'Documentation is Complete'}
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td>
                        <Submit className="is-dark is-fullwidth" name="action" value="PAYMENT_STATUS_PAID">
                          Mark Order as Paid
                        </Submit>
                      </td>
                      <td>Update the Order as Payment Received</td>
                    </tr>
                  )}
                  <tr>
                    <td>
                      <Submit className="is-warning is-fullwidth" name="action" value="REJECT_AND_CONTACT_CUSTOMER">
                        {recoveryCertificate?.paymentStatus === 'PAID'
                          ? 'Reject & Contact Customer'
                          : 'Customer Contacted'}
                      </Submit>
                    </td>
                    <td>Contact Customer for more Information</td>
                  </tr>
                  <tr>
                    <td>
                      <Submit className="is-danger is-fullwidth" name="action" value="CANCEL_ORDER">
                        Cancel Order
                      </Submit>
                    </td>
                    <td>Cancel and Refund Customer</td>
                  </tr>
                  <tr>
                    <td>
                      <Submit className="is-info is-fullwidth" name="action" value="UNCLAIM">
                        Update & Unclaim
                      </Submit>
                    </td>
                    <td>Save details and unclaim</td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <hr className="m-0" />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Submit className="is-primary is-fullwidth" name="action" value="PREVIEW">
                        Preview Recovery Certificate
                      </Submit>
                    </td>
                    <td>Preview Certificate that will be sent to the customer</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>

          <CertificateNotes recoveryCertificate={recoveryCertificate} edit={true} />

          <CertificateFiles recoveryCertificate={recoveryCertificate} edit={true} />
        </div>
      </div>
    </>
  )
}

export default VerifyRecoveryCertificateModal
